import React, { useCallback, useMemo } from "react";

import Menu from "antd/es/menu";
import { useIntl } from "react-intl";

import { Deal } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";

import BoardColumn from "@app/component/DnDBoard/types/BoardColumn";
import { StagesWithDeals } from "@app/scene/deal/store/types/KanbanView";
import StageColumn from "@app/scene/deal/type/StageColumn";
import { dealKanbanViewAnalytics } from "@app/scene/deal/util/dealTableAnalytics";

const useBoardColumns = (
  groupedDeals: StagesWithDeals,
  stages: Stage[],
  isMember: boolean,
  setSelectedStageForDownload: (stage: Stage) => void,
  onOpenFunnelSettings?: (funnelId: Funnel["id"], stageId?: Stage["id"]) => void,
  options?: { canDownload?: boolean }
): BoardColumn<StageColumn, Deal>[] => {
  const intl = useIntl();

  const handleExportClick = useCallback(
    (stage: Stage) => {
      setSelectedStageForDownload(stage);
      dealKanbanViewAnalytics?.clicked(["Export Column Data"], { column: stage.name });
    },
    [setSelectedStageForDownload]
  );

  return useMemo(
    () =>
      stages.map((stage) => {
        const columnDeals = groupedDeals[stage.id]?.deals ?? [];
        return {
          id: stage.id,
          color: stage.color,
          data: {
            adjustedAmount: groupedDeals[stage.id]?.adjustedAmount,
            amount: groupedDeals[stage.id]?.amount,
            stage,
          },
          items: columnDeals,
          loading: groupedDeals[stage.id]?.loading,
          menuItems: [
            ...(isMember
              ? []
              : [
                  <Menu.Item
                    key="stagePreferences"
                    onClick={() => onOpenFunnelSettings?.(stage.funnel.id, stage.id)}
                  >
                    {intl.formatMessage({
                      id: "deals.kanbanView.columnMenu.stagePreferences",
                      defaultMessage: "Open stage preferences",
                      description: "Deal DND board column menu - Stage Preferences",
                    })}
                  </Menu.Item>,
                ]),
            ...(options?.canDownload === false
              ? []
              : [
                  <Menu.Item key="export" onClick={() => handleExportClick(stage)}>
                    {intl.formatMessage({
                      id: "deals.kanbanView.columnMenu.exportData",
                      defaultMessage: "Download",
                      description: "Deal DND board column menu - Export Data",
                    })}
                  </Menu.Item>,
                ]),
          ],
          name: stage.name,
          total: groupedDeals[stage.id]?.total,
        };
      }),
    [
      stages,
      groupedDeals,
      isMember,
      intl,
      onOpenFunnelSettings,
      handleExportClick,
      options?.canDownload,
    ]
  );
};

export default useBoardColumns;
